import * as React from 'react';

import {
  loaderVariants,
  spinnerVariants,
  spinnerSvgVariants,
  spinnerCircleVariants,
  spinnerProgressVariants,
  loaderLabelVariants,
  loaderValueVariants,
} from './Loader.styles';
import { cn } from '../../../lib/utils';

import type { LoaderProps, SpinnerProps, LoaderLabelProps, LoaderValueProps } from './Loader.types';

/**
 * Spinner Component
 *
 * Animated circular progress indicator with filled donut-style ring matching Figma design.
 * Uses SVG mask to create the donut effect and path for the progress arc.
 *
 * @example
 * ```tsx
 * <Spinner size="m" type="indeterminate" active />
 * <Spinner size="m" type="determinate" value={65} />
 * ```
 */
const Spinner = React.forwardRef<SVGSVGElement, SpinnerProps>(
  (
    {
      className,
      size = 'm',
      type = 'indeterminate',
      value = 0,
      active = true,
      inline = false,
      ...props
    },
    ref
  ) => {
    // Calculate progress for determinate types
    const progress = Math.min(100, Math.max(0, value));

    // Calculate stroke-dasharray and stroke-dashoffset for progress
    // Circumference = 2 * π * r, where r = 44 (center at 48, radius adjusted for 8px stroke)
    const radius = 44;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset =
      type === 'indeterminate'
        ? circumference * 0.75 // Show 25% arc for indeterminate
        : circumference * (1 - progress / 100);

    return (
      <div className={cn(spinnerVariants({ size, inline }))}>
        <svg
          ref={ref}
          className={cn(
            spinnerSvgVariants({
              active: active && type === 'indeterminate',
              type,
            }),
            className
          )}
          viewBox="0 0 96 96"
          xmlns="http://www.w3.org/2000/svg"
          role="status"
          aria-label={type === 'indeterminate' ? 'Loading' : `Loading: ${progress}% complete`}
          {...props}
        >
          {/* Background circle (full track) */}
          <circle
            cx="48"
            cy="48"
            r={radius}
            fill="none"
            stroke="#dedede"
            strokeWidth="8"
            className={cn(spinnerCircleVariants())}
          />

          {/* Progress arc */}
          {(type !== 'indeterminate' || active) && (
            <circle
              cx="48"
              cy="48"
              r={radius}
              fill="none"
              stroke="#096151"
              strokeWidth="8"
              strokeLinecap="round"
              className={cn(spinnerProgressVariants({ type }))}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 48 48)"
            />
          )}
        </svg>
        {type === 'determinate-value' && !inline && <LoaderValue value={value} />}
      </div>
    );
  }
);

Spinner.displayName = 'Spinner';

/**
 * LoaderValue Component
 *
 * Displays percentage value inside the spinner for determinate-value type.
 *
 * @example
 * ```tsx
 * <LoaderValue value={65} />
 * ```
 */
const LoaderValue = React.forwardRef<HTMLDivElement, LoaderValueProps>(
  ({ className, value, ...props }, ref) => {
    // Clamp value between 0 and 100
    const clampedValue = Math.max(0, Math.min(100, value));

    return (
      <div ref={ref} className={cn(loaderValueVariants(), className)} aria-hidden="true" {...props}>
        {Math.round(clampedValue)}%
      </div>
    );
  }
);

LoaderValue.displayName = 'LoaderValue';

/**
 * LoaderLabel Component
 *
 * Text label displayed below the spinner in non-inline mode.
 *
 * @example
 * ```tsx
 * <LoaderLabel>Loading data...</LoaderLabel>
 * ```
 */
const LoaderLabel = React.forwardRef<HTMLParagraphElement, LoaderLabelProps>(
  ({ className, children, ...props }, ref) => (
    <p ref={ref} className={cn(loaderLabelVariants(), className)} {...props}>
      {children}
    </p>
  )
);

LoaderLabel.displayName = 'LoaderLabel';

/**
 * Loader Component
 *
 * A flexible loader component supporting indeterminate and determinate
 * progress states with optional labels and percentage displays.
 *
 * @example
 * ```tsx
 * // Indeterminate spinner
 * <Loader size="m" type="indeterminate" label="Loading..." />
 *
 * // Determinate progress
 * <Loader size="m" type="determinate" value={65} label="Uploading..." />
 *
 * // With percentage display
 * <Loader size="m" type="determinate-value" value={65} label="Processing..." />
 *
 * // Inline mode
 * <Loader size="m" inline />
 * ```
 */
const Loader = React.forwardRef<HTMLDivElement, LoaderProps>(
  (
    {
      className,
      size = 'm',
      type = 'indeterminate',
      inline = false,
      value = 0,
      label,
      active = true,
      ...props
    },
    ref
  ) => (
    <div ref={ref} className={cn(loaderVariants({ inline }), className)} {...props}>
      <Spinner size={size} type={type} value={value} active={active} inline={inline} />
      {!inline && label && <LoaderLabel>{label}</LoaderLabel>}
    </div>
  )
);

Loader.displayName = 'Loader';

// Attach sub-components to main component
export const LoaderComponent = Object.assign(Loader, {
  Spinner,
  Value: LoaderValue,
  Label: LoaderLabel,
});

export { Loader, Spinner, LoaderValue, LoaderLabel };
